import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer, Typography, Button, Divider } from '@mui/material';
import { Icon } from '@iconify/react';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
// components
// import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
//
import navConfig from './NavConfig';
import NavbarDocs from './NavbarDocs';
import NavbarAccount from './NavbarAccount';
import CollapseButton from './CollapseButton';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();

  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center">
            <Box sx={{ width: 40, height: 40 }}>
              <img src="/logo/peerxiv_logo.png" alt="logo" />
            </Box>
            {!isCollapse && <Typography variant="h6">PeerXiv Mock</Typography>}
          </Stack>

          {isDesktop && !isCollapse && (
            <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          )}
        </Stack>

        <NavbarAccount isCollapse={isCollapse} />
      </Stack>

      <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />

      <Box sx={{ flexGrow: 1 }} />

      <Stack
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ px: 5, mt: 2, width: 1, textAlign: 'center', display: 'block' }}
      >
        <Divider />
        <Typography variant="body2">
          Like what you see? PeerXiv is currently just a mock, but with your help it can become a reality!
        </Typography>
        <Button
          sx={{ height: '2em', width: '100%', my: 1 }}
          variant="outlined"
          href="https://twitter.com/peerxiv"
          color="primary"
          startIcon={<Icon icon={'eva:twitter-fill'} />}
        >
          Follow On Twitter
        </Button>
        <Button
          sx={{ height: '2em', width: '100%', my: 1 }}
          variant="outlined"
          color="primary"
          href="https://forms.gle/ivdJvh2gMnTCuakE9"
          startIcon={<Icon icon={'emojione-monotone:pear'} />}
        >
          Become a PeerXiver
        </Button>
        <Button
          sx={{ height: '2em', width: '100%', my: 1 }}
          variant="outlined"
          color="primary"
          href="mailto:peerxiv@gmail.com"
          startIcon={<Icon icon={'bxl:gmail'} />}
        >
          Contact Us
        </Button>
      </Stack>

      {!isCollapse && <NavbarDocs />}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}

// <Stack direction="row" alignItems="center" justifyContent="space-between">
//     <RouterLink to="/">
//         <Box sx={{ width: 40, height: 40 }}>
//             <img src="https://i1.sndcdn.com/avatars-000236214492-qp1pm1-t500x500.jpg" alt="logo" />
//         </Box>
//     </RouterLink>
//     <Typography variant="h6">PeerXiv</Typography>
// </Stack>
